import type { ProductMeasurementsBlockProps } from '../ProductMeasurementsBlock';
import type { ProductOverlayDialogProps } from '../productOverlayDialog/ProductOverlayDialog';
import type { PropsWithChildren } from 'react';

import dynamic from 'next/dynamic';
import { useState, useMemo, useRef } from 'react';

import { useProductFetch } from './hooks/useProductFetch';
import { useSelectSize } from './hooks/useSelectSize';
import { useSizeButtonsStyle } from './hooks/useSizeButtonStyle';
import { useSizeSelectorInitialize } from './hooks/useSizeSelectorInitialize';
import { useStockLevelMessage } from './hooks/useStockLevelMessage';
import { SizeBlockType } from './SizeBlockHeader';
import { SizeItem } from './SizeItem';
import { StockAvailabilityMessage } from './StockAvailabilityMessage';
import { S } from './styles';
import { createSizeText } from './utils/createSizeText';
import { shortenSize } from './utils/shortenSize';
import { ProductAddActions } from '../ProductAddActions';
import { ProductShippingEstimates } from '../ProductShippingEstimates';
import { SizeGuideBlock } from '../SizeGuideBlock';
import { WishlistActionType } from '../WishlistActionType.enum';
import type { ProductCountdownTimer } from '~/amplienceTypes/schemas/imported/product-countdown-timer-schema';
import type { ProductOverlay } from '~/amplienceTypes/schemas/imported/product-overlay-schema';
import type { ProductSizeGuide as ProductSizeGuideSchema } from '~/amplienceTypes/schemas/imported/product-size-guide-schema';
import type { ProductSizeSelector } from '~/amplienceTypes/schemas/imported/product-size-selector-schema';
import { useCookieContext } from '~/utilities/context/dynamic/CookieContext';
import { useLayoutDesignContext } from '~/utilities/context/dynamic/LayoutDesignContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useSizeContext } from '~/utilities/context/dynamic/SizeContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import type { Size, Product } from '~/utilities/graphql/codegen';
import { SiteType, SizeType } from '~/utilities/graphql/codegen';
import { checkSizeFormatted } from '~/utilities/parsers';

const ProductOverlayDialog = dynamic<ProductOverlayDialogProps & ProductOverlay>(
  () =>
    import('../productOverlayDialog/ProductOverlayDialog').then(mod => mod.ProductOverlayDialog),
  { ssr: false }
);

const ProductMeasurementsBlock = dynamic<ProductMeasurementsBlockProps>(
  () => import('../ProductMeasurementsBlock').then(mod => mod.ProductMeasurementsBlock),
  { ssr: false }
);

const SkipButtonStyling: Record<SiteType, boolean> = {
  [SiteType.Aaf]: true,
  [SiteType.Employeeshop]: false,
  [SiteType.Gstar]: false,
  [SiteType.Outlet]: false,
  [SiteType.Wahts]: false,
};

const ShowSizeGuideBlock: Record<SiteType, boolean> = {
  [SiteType.Aaf]: true,
  [SiteType.Employeeshop]: false,
  [SiteType.Gstar]: false,
  [SiteType.Outlet]: false,
  [SiteType.Wahts]: false,
};

const ShowStockAvailability: Record<SiteType, boolean> = {
  [SiteType.Aaf]: false,
  [SiteType.Employeeshop]: true,
  [SiteType.Gstar]: true,
  [SiteType.Outlet]: true,
  [SiteType.Wahts]: true,
};

const ShowProductMeasurements: Record<SiteType, boolean> = {
  [SiteType.Aaf]: false,
  [SiteType.Employeeshop]: true,
  [SiteType.Gstar]: true,
  [SiteType.Outlet]: true,
  [SiteType.Wahts]: false,
};

interface ProductSizeSelectorProps
  extends ProductSizeSelector,
    ProductSizeGuideSchema,
    PropsWithChildren {
  productCountdownTimer?: {
    content?: ProductCountdownTimer;
    [k: string]: unknown;
  };
  isOutOfStockSizeSelected: boolean;
  setIsOutOfStockSizeSelected: (value: boolean) => void;
  setHasLocalStoreStock: (value: boolean) => void;
  setSelectedSize: (value?: Size | null) => void;
  selectedSize: Size | null | undefined;
  disableBackInStockPopup?: boolean;
}

export const SizeSelector = ({
  sizeLabel = '',
  productAddActions,
  overlay,
  waistLabel = '',
  lengthLabel = '',
  productMeasurements,
  productCountdownTimer,
  isOutOfStockSizeSelected,
  selectedSize,
  setIsOutOfStockSizeSelected,
  setHasLocalStoreStock,
  setSelectedSize,
  disableBackInStockPopup = false,
  children,
  ...props
}: ProductSizeSelectorProps) => {
  const { country, siteType } = useAppContext();
  const { product } = usePDPContext();
  const { dynamicProductData } = usePDPDynamicContext();
  const { hasProductAccordions } = useLayoutDesignContext();
  const { isMobileOrTablet, isDesktop } = useMediaQueryContext();
  const { sizeCode, setSizeCode, gridValues, setGridValues } = useSizeContext();
  const { cocoCookie, oneTrustCookie } = useCookieContext();

  const sizeInformation = dynamicProductData.sizeInformation ?? product.sizeInformation;
  const stockInformation = dynamicProductData.stockInformation ?? product.stockInformation;
  const comingSoon = dynamicProductData.comingSoon ?? product.comingSoon;

  const sizes = useMemo(() => sizeInformation?.sizes || [], [sizeInformation]);
  const sizes1WrapperRef = useRef<HTMLDivElement>(null);
  const sizes2WrapperRef = useRef<HTMLDivElement>(null);

  const [backInStockDialogOpen, setBackInStockDialogOpen] = useState(false);

  const isDoubleSize = sizeInformation?.sizeType === SizeType.NumericDouble;

  const showProductMeasurements =
    product.shouldRequestSizeMeasurements &&
    productMeasurements &&
    isDoubleSize &&
    (isMobileOrTablet ||
      (isDesktop &&
        hasProductAccordions &&
        (productMeasurements as ProductMeasurementsBlockProps)?.content?.visibility?.desktop ===
          true));
  const cookieConsentExists = !!cocoCookie || !!oneTrustCookie;

  const { currentSizeVariantProduct } = useProductFetch({ setHasLocalStoreStock });

  const { stockLevelMessage } = useStockLevelMessage();
  const {
    selectSize,
    selectDoubleSize,
    filteredParsedLengthSizes,
    filteredParsedWaistSizes,
    parsedLengthSizes,
    filteredSizes,
    activatable,
    setActivatable,
  } = useSelectSize({
    disableBackInStockPopup,
    isDoubleSize,
    setSelectedSize,
    setIsOutOfStockSizeSelected,
    setBackInStockDialogOpen,
    sizes,
  });

  const { isSizeFormatted, sizeButtonStyle } = checkSizeFormatted(sizes, country);

  useSizeButtonsStyle({
    parrentRef: sizes1WrapperRef,
    minWidth: isSizeFormatted ? 56 : 48,
    skip: SkipButtonStyling[siteType],
    selectedSize,
    parsedLengthSizes,
  });
  useSizeButtonsStyle({
    parrentRef: sizes2WrapperRef,
    minWidth: isSizeFormatted ? 56 : 48,
    skip: SkipButtonStyling[siteType],
    selectedSize,
    parsedLengthSizes,
  });

  useSizeSelectorInitialize({
    setActivatable,
    setBackInStockDialogOpen,
  });

  const renderSizes = () => {
    if (comingSoon) {
      return <></>;
    }

    if (product.oneSize) {
      return (
        <S.SizeWrapper>
          <S.SizeWrapperHeader>
            <SizeBlockType id="size-width-label" label={sizeLabel} testId="size-width-label" />

            {children}
          </S.SizeWrapperHeader>

          <S.Sizes
            ref={sizes1WrapperRef}
            data-testid="width-size-selector"
            aria-labelledby="size-width-label"
            role="group"
          >
            {filteredSizes.map((size, i) => (
              <SizeItem
                key={i}
                activatable={activatable}
                currentGridValue={null}
                displayValue={props.oneSizeTextV2}
                gridValue={null}
                isSizeFormatted={isSizeFormatted}
                label={props.oneSizeTextV2}
                onClick={() => selectSize(size)}
                purchasable={size?.purchasable}
                sizeButtonStyle={sizeButtonStyle}
                testId="width-size"
                willBeAvailable={size?.willBeAvailable}
              />
            ))}
          </S.Sizes>
        </S.SizeWrapper>
      );
    }

    if (isDoubleSize) {
      return (
        <>
          <S.SizeWrapper>
            <S.SizeWrapperHeader>
              <SizeBlockType id="size-width-label" label={waistLabel} testId="size-width-label" />

              {children}
            </S.SizeWrapperHeader>

            <S.Sizes
              ref={sizes1WrapperRef}
              data-testid="width-size-selector"
              aria-labelledby="size-width-label"
              role="group"
            >
              {filteredParsedWaistSizes.map((size, i) => (
                <SizeItem
                  key={i}
                  activatable={activatable}
                  currentGridValue={gridValues.gridValue1}
                  displayValue={size.value}
                  gridValue={size.value.toUpperCase()}
                  isSizeFormatted={isSizeFormatted}
                  label={`${waistLabel} ${size.value}`}
                  onClick={() => selectDoubleSize(size.value, size, true)}
                  purchasable={size.purchasable}
                  sizeButtonStyle={sizeButtonStyle}
                  subText={createSizeText(
                    size?.formattedValue?.replace(/(^.*\(|\).*)/g, '') || '',
                    true
                  )}
                  testId="width-size"
                  willBeAvailable={size.willBeAvailable}
                />
              ))}
            </S.Sizes>
          </S.SizeWrapper>
          <S.SizeWrapper>
            <S.SizeWrapperHeader>
              <SizeBlockType
                id="size-length-label"
                label={lengthLabel}
                testId="size-length-label"
              />
            </S.SizeWrapperHeader>
            <S.Sizes
              ref={sizes2WrapperRef}
              role="group"
              data-testid="length-size-selector"
              aria-labelledby="size-length-label"
            >
              {filteredParsedLengthSizes.map((size, i) => (
                <SizeItem
                  key={i}
                  activatable={activatable}
                  currentGridValue={gridValues.gridValue2}
                  displayValue={size.value}
                  gridValue={size.value.toUpperCase()}
                  isSizeFormatted={isSizeFormatted}
                  label={`${lengthLabel} ${size.value}`}
                  onClick={() => selectDoubleSize(size.value, size, false)}
                  purchasable={size.purchasable}
                  sizeButtonStyle={sizeButtonStyle}
                  testId="length-size"
                  willBeAvailable={size.willBeAvailable}
                />
              ))}
            </S.Sizes>
          </S.SizeWrapper>
        </>
      );
    }

    return (
      <S.SizeWrapper>
        <S.SizeWrapperHeader>
          <SizeBlockType id="size-width-label" label={sizeLabel} testId="size-width-label" />

          {children}
        </S.SizeWrapperHeader>

        <S.Sizes
          ref={sizes1WrapperRef}
          data-testid="width-size-selector"
          aria-labelledby="size-width-label"
          role="group"
        >
          {filteredSizes.map((size, i) => (
            <SizeItem
              key={i}
              activatable={activatable}
              currentGridValue={gridValues.gridValue1}
              displayValue={shortenSize(size?.gridValue1 ?? '')}
              gridValue={size?.gridValue1?.toUpperCase()}
              isSizeFormatted={isSizeFormatted}
              label={`${sizeLabel} ${size?.gridValue1}`}
              onClick={() => selectSize(size)}
              purchasable={size?.purchasable}
              sizeButtonStyle={sizeButtonStyle}
              subText={size?.formattedValue?.replace(/(^.*\(|\).*)/g, '')}
              testId="width-size"
              willBeAvailable={size?.willBeAvailable}
            />
          ))}
        </S.Sizes>
      </S.SizeWrapper>
    );
  };

  return (
    <S.Wrapper>
      {!product.isGiftCard && sizeLabel && (
        <>
          {showProductMeasurements && ShowProductMeasurements[siteType] && (
            <ProductMeasurementsBlock {...productMeasurements} />
          )}
          {renderSizes()}
        </>
      )}

      {!isOutOfStockSizeSelected &&
        stockLevelMessage.key &&
        !comingSoon &&
        ShowStockAvailability[siteType] && (
          <StockAvailabilityMessage
            stockLevelMessage={stockLevelMessage}
            countdownTimerContent={productCountdownTimer?.content}
            stockInformation={stockInformation}
            salesDiscountsEndDate={product?.salesDiscountsEndDate}
          />
        )}
      {!isOutOfStockSizeSelected && <ProductShippingEstimates />}
      {ShowSizeGuideBlock[siteType] && !comingSoon && !product.isGiftCard && !product.oneSize && (
        <SizeGuideBlock labelProps={props} />
      )}
      {!isOutOfStockSizeSelected && productAddActions?.content && (
        <ProductAddActions
          sizeCode={sizeCode}
          gridValues={gridValues}
          product={currentSizeVariantProduct}
          setDlgOpen={setBackInStockDialogOpen}
          icon="shoppingBag"
          wishlistButtonType={WishlistActionType.PDP}
          trackFitAnalyticsSizes
          algoliaAddToCartEventName="PDP_addedToCartObjectIDs"
          algoliaAddToCartAfterSearchEventName="PDP_addedToCartObjectIDsAfterSearch"
          {...productAddActions.content}
        />
      )}

      {!product.isGiftCard && backInStockDialogOpen && overlay && cookieConsentExists && (
        <ProductOverlayDialog
          onClose={() => {
            setBackInStockDialogOpen(false);

            if (!activatable) {
              setGridValues({ gridValue1: '', gridValue2: '' });
              setSizeCode('');
            }

            setActivatable(true);
          }}
          waistLabel={waistLabel}
          lengthLabel={lengthLabel}
          sizeLabel={sizeLabel}
          product={{ ...product, ...dynamicProductData } as Product}
          {...overlay.content}
        />
      )}
    </S.Wrapper>
  );
};
